import {
    Accordion,
    Badge,
    Button,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    Switch,
    TagsInput,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useState, type FC } from 'react';

import type {
    AvailableChannel,
    ChannelWarehouseOptions,
} from '@api/distribution';
import {
    getChannelWarehouseOptions,
    getDistributionChannels,
    upsertDistributionEmailConfig,
} from '@api/distribution';

const PLACEHOLDER_HINT =
    'Placeholders: {source} {destination} {orderId} {skuCount} {qty} {measurement} {dispatchDate} {receivingDate}';

const extractErrorMessage = (err: unknown): string => {
    const data = (err as any)?.error?.data;
    if (data) {
        const msg = data.message;
        if (Array.isArray(msg) && msg.length > 0) {
            const first = msg[0];
            if (first?.constraints) return Object.values(first.constraints).join(', ');
        }
        if (typeof msg === 'string') return msg;
    }
    return (err as any)?.error?.message ?? (err instanceof Error ? err.message : 'Save failed');
};

interface EditorProps {
    channel: AvailableChannel;
    onSaved: (channel: AvailableChannel) => void;
}

const EmailConfigEditor: FC<EditorProps> = ({ channel, onSaved }) => {
    const cfg = channel.config?.emailConfig ?? null;

    const [loading, setLoading] = useState(true);
    const [whOptions, setWhOptions] = useState<ChannelWarehouseOptions | null>(null);

    const [enabled, setEnabled] = useState(cfg?.enabled ?? false);
    const [signature, setSignature] = useState(cfg?.signature ?? '');
    const [replyTo, setReplyTo] = useState(cfg?.replyTo ?? '');
    const [subjectTemplate, setSubjectTemplate] = useState(cfg?.subjectTemplate ?? '');
    const [introLine, setIntroLine] = useState(cfg?.introLine ?? '');
    const [instructionText, setInstructionText] = useState((cfg?.instructionLines ?? []).join('\n'));
    const [alwaysTo, setAlwaysTo] = useState<string[]>(cfg?.alwaysTo ?? []);
    const [alwaysCc, setAlwaysCc] = useState<string[]>(cfg?.alwaysCc ?? []);
    const [byDestination, setByDestination] = useState<Record<string, { to: string[]; cc: string[] }>>(
        cfg?.byDestination ?? {},
    );
    const [bySource, setBySource] = useState<Record<string, { shortCode: string; extraCc: string[]; to?: string[] }>>(
        cfg?.bySourceWarehouse ?? {},
    );

    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getChannelWarehouseOptions(channel.channelAbbreviationKey)
            .then(setWhOptions)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const touch = () => setSaved(false);

    const setDest = (id: string, patch: Partial<{ to: string[]; cc: string[] }>) => {
        touch();
        setByDestination((prev) => ({
            ...prev,
            [id]: { to: patch.to ?? prev[id]?.to ?? [], cc: patch.cc ?? prev[id]?.cc ?? [] },
        }));
    };
    const setSrc = (id: string, patch: Partial<{ shortCode: string; extraCc: string[]; to?: string[] }>) => {
        touch();
        setBySource((prev) => ({
            ...prev,
            [id]: {
                shortCode: patch.shortCode ?? prev[id]?.shortCode ?? '',
                extraCc: patch.extraCc ?? prev[id]?.extraCc ?? [],
                to: patch.to ?? prev[id]?.to ?? [],
            },
        }));
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const instructionLines = instructionText
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean);

            const cleanDest: Record<string, { to: string[]; cc: string[] }> = {};
            for (const [id, v] of Object.entries(byDestination)) {
                if ((v.to?.length ?? 0) > 0 || (v.cc?.length ?? 0) > 0) {
                    cleanDest[id] = { to: v.to ?? [], cc: v.cc ?? [] };
                }
            }
            const cleanSrc: Record<string, { shortCode: string; extraCc: string[]; to?: string[] }> = {};
            for (const [id, v] of Object.entries(bySource)) {
                const code = (v.shortCode ?? '').trim();
                if (code.length > 0 || (v.extraCc?.length ?? 0) > 0 || (v.to?.length ?? 0) > 0) {
                    cleanSrc[id] = { shortCode: code, extraCc: v.extraCc ?? [], to: v.to ?? [] };
                }
            }

            const updated = await upsertDistributionEmailConfig(channel.channelAbbreviationKey, {
                enabled,
                signature: signature.trim() ? signature.trim() : null,
                replyTo: replyTo.trim() ? replyTo.trim() : null,
                subjectTemplate,
                introLine,
                instructionLines,
                alwaysTo,
                alwaysCc,
                byDestination: cleanDest,
                bySourceWarehouse: cleanSrc,
            });
            onSaved({ ...channel, config: updated });
            setSaved(true);
        } catch (err: unknown) {
            setError(extractErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loader size="sm" />;

    const destinations = whOptions?.channelWarehouses ?? [];
    const sources = whOptions?.internalWarehouses ?? [];

    return (
        <Stack gap="md" mt="sm">
            <Switch
                checked={enabled}
                onChange={(e) => {
                    touch();
                    setEnabled(e.currentTarget.checked);
                }}
                label="Enable plan emails for this channel"
                description="When off, the Email button stays hidden on this channel's plans."
            />

            <Accordion multiple defaultValue={['template', 'always']} variant="separated" radius="md">
                <Accordion.Item value="template">
                    <Accordion.Control>Template</Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="sm">
                            <TextInput
                                label="Subject template"
                                description={PLACEHOLDER_HINT}
                                value={subjectTemplate}
                                onChange={(e) => {
                                    touch();
                                    setSubjectTemplate(e.currentTarget.value);
                                }}
                            />
                            <Textarea
                                label="Intro line"
                                description="Opening line above the data block. Same placeholders."
                                autosize
                                minRows={2}
                                value={introLine}
                                onChange={(e) => {
                                    touch();
                                    setIntroLine(e.currentTarget.value);
                                }}
                            />
                            <Textarea
                                label="Instruction lines"
                                description="One per line. Shown below the Order ID / SKUs / Qty / Measurement block."
                                autosize
                                minRows={3}
                                value={instructionText}
                                onChange={(e) => {
                                    touch();
                                    setInstructionText(e.currentTarget.value);
                                }}
                            />
                            <Textarea
                                label="Signature"
                                description="Printed under 'Regards,'. Can span lines (name, email). Leave blank to fall back to the sender's name."
                                autosize
                                minRows={1}
                                value={signature}
                                onChange={(e) => {
                                    touch();
                                    setSignature(e.currentTarget.value);
                                }}
                            />
                            <TextInput
                                label="Reply-to"
                                description="Replies go to this address. Leave blank to reply to whoever sends."
                                placeholder="name@company.com"
                                value={replyTo}
                                onChange={(e) => {
                                    touch();
                                    setReplyTo(e.currentTarget.value);
                                }}
                            />
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="always">
                    <Accordion.Control>Always-on recipients</Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="sm">
                            <TagsInput
                                label="Always To"
                                description="Added to every email's To. Type an address and press Enter."
                                placeholder="name@company.com"
                                value={alwaysTo}
                                onChange={(v) => {
                                    touch();
                                    setAlwaysTo(v);
                                }}
                            />
                            <TagsInput
                                label="Always CC"
                                description="Added to every email's CC."
                                placeholder="name@company.com"
                                value={alwaysCc}
                                onChange={(v) => {
                                    touch();
                                    setAlwaysCc(v);
                                }}
                            />
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="destinations">
                    <Accordion.Control>Per-destination recipients</Accordion.Control>
                    <Accordion.Panel>
                        {destinations.length === 0 ? (
                            <Text size="sm" c="dimmed">No destination warehouses for this channel.</Text>
                        ) : (
                            <Stack gap="sm">
                                {destinations.map((wh) => (
                                    <Paper key={wh.warehouseId} withBorder p="sm" radius="md">
                                        <Text size="sm" fw={600} mb={6}>{wh.name}</Text>
                                        <Stack gap="xs">
                                            <TagsInput
                                                label="To"
                                                placeholder="store@company.com"
                                                value={byDestination[wh.warehouseId]?.to ?? []}
                                                onChange={(v) => setDest(wh.warehouseId, { to: v })}
                                            />
                                            <TagsInput
                                                label="CC"
                                                placeholder="manager@company.com"
                                                value={byDestination[wh.warehouseId]?.cc ?? []}
                                                onChange={(v) => setDest(wh.warehouseId, { cc: v })}
                                            />
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="sources">
                    <Accordion.Control>Per-source warehouse</Accordion.Control>
                    <Accordion.Panel>
                        {sources.length === 0 ? (
                            <Text size="sm" c="dimmed">No source warehouses for this channel.</Text>
                        ) : (
                            <Stack gap="sm">
                                {sources.map((wh) => (
                                    <Paper key={wh.warehouseId} withBorder p="sm" radius="md">
                                        <Text size="sm" fw={600} mb={6}>{wh.name}</Text>
                                        <Stack gap="xs">
                                            <TextInput
                                                label="Short code"
                                                description="Fills {source} in the subject, e.g. HYD."
                                                value={bySource[wh.warehouseId]?.shortCode ?? ''}
                                                onChange={(e) => setSrc(wh.warehouseId, { shortCode: e.currentTarget.value })}
                                            />
                                            <TagsInput
                                                label="To"
                                                description="Added to To when this warehouse is the source (e.g. the city coordinator)."
                                                placeholder="coordinator@company.com"
                                                value={bySource[wh.warehouseId]?.to ?? []}
                                                onChange={(v) => setSrc(wh.warehouseId, { to: v })}
                                            />
                                            <TagsInput
                                                label="Extra CC"
                                                description="Added to CC when this warehouse is the source (e.g. 3PL staff)."
                                                placeholder="logistics@company.com"
                                                value={bySource[wh.warehouseId]?.extraCc ?? []}
                                                onChange={(v) => setSrc(wh.warehouseId, { extraCc: v })}
                                            />
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            {error && <Text size="sm" c="red">{error}</Text>}

            <Group gap="sm">
                <Button onClick={save} loading={saving}>Save</Button>
                {saved && (
                    <Group gap={4} c="green">
                        <IconCheck size={16} />
                        <Text size="sm">Saved</Text>
                    </Group>
                )}
            </Group>
        </Stack>
    );
};

const DistributionEmailTab: FC = () => {
    const [channels, setChannels] = useState<AvailableChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    useEffect(() => {
        getDistributionChannels()
            .then((data) => {
                setChannels(data);
                setSelectedKey((prev) => prev ?? data[0]?.channelAbbreviationKey ?? null);
            })
            .catch(() => setChannels([]))
            .finally(() => setLoading(false));
    }, []);

    const handleSaved = (updated: AvailableChannel) => {
        setChannels((prev) =>
            prev.map((ch) => (ch.channelAbbreviationKey === updated.channelAbbreviationKey ? updated : ch)),
        );
    };

    const selectedChannel = channels.find((c) => c.channelAbbreviationKey === selectedKey) ?? null;

    if (loading) return <Loader size="sm" />;

    if (channels.length === 0) {
        return <Text size="sm" c="dimmed">No outbound channels found. Ensure external warehouses have a channel abbreviation key set.</Text>;
    }

    return (
        <Stack gap="lg">
            <div>
                <Text size="sm" fw={500} mb="xs">Select a channel to configure its plan emails</Text>
                <Group gap="xs" wrap="wrap">
                    {channels.map((ch) => (
                        <Badge
                            key={ch.channelAbbreviationKey}
                            size="lg"
                            variant={selectedKey === ch.channelAbbreviationKey ? 'filled' : 'outline'}
                            color={ch.config?.emailConfig?.enabled ? 'blue' : 'gray'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedKey(ch.channelAbbreviationKey)}
                        >
                            {ch.channelAbbreviationKey}
                            {ch.config?.emailConfig?.enabled && ' · on'}
                        </Badge>
                    ))}
                </Group>
            </div>

            {selectedChannel && (
                <>
                    <Divider />
                    <div>
                        <Group gap="xs" mb={4}>
                            <Title order={5}>{selectedChannel.channelAbbreviationKey}</Title>
                            <Badge
                                size="xs"
                                variant="light"
                                color={selectedChannel.config?.emailConfig?.enabled ? 'green' : 'gray'}
                            >
                                {selectedChannel.config?.emailConfig?.enabled ? 'Emails on' : 'Emails off'}
                            </Badge>
                        </Group>
                        <EmailConfigEditor
                            key={selectedChannel.channelAbbreviationKey}
                            channel={selectedChannel}
                            onSaved={handleSaved}
                        />
                    </div>
                </>
            )}
        </Stack>
    );
};

export default DistributionEmailTab;
